import { useRef, useState, useCallback, useEffect } from 'react';
import { Minus, Square, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { WindowState } from './DesktopContext';
import { useDesktop } from './DesktopContext';
import { getAppMeta } from './appRegistry';

interface AppWindowProps {
  window: WindowState;
  children: React.ReactNode;
}

/** Height reserved for the dock at the bottom of the screen */
const DOCK_HEIGHT = 64;
/** Distance from a screen edge (px) that triggers a snap target while dragging */
const SNAP_THRESHOLD = 16;
/** Breathing room around edge-snapped windows (OS-style tiled margins) */
const SNAP_GAP = 8;
const MIN_WIDTH = 320;
const MIN_HEIGHT = 240;

type SnapTarget = 'left' | 'right' | 'top' | null;

function getSnapGeometry(target: Exclude<SnapTarget, null>) {
  const w = window.innerWidth;
  const h = window.innerHeight - DOCK_HEIGHT;
  switch (target) {
    case 'left':
      return { x: SNAP_GAP, y: SNAP_GAP, width: Math.floor(w / 2) - SNAP_GAP * 1.5, height: h - SNAP_GAP * 2 };
    case 'right':
      return { x: Math.floor(w / 2) + SNAP_GAP / 2, y: SNAP_GAP, width: Math.ceil(w / 2) - SNAP_GAP * 1.5, height: h - SNAP_GAP * 2 };
    case 'top':
      return { x: 0, y: 0, width: w, height: h };
  }
}

export function AppWindow({ window: win, children }: AppWindowProps) {
  const { activeWindowId, closeWindow, minimizeWindow, maximizeWindow, restoreWindow, focusWindow, updateWindowGeometry } = useDesktop();
  const windowRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [snapTarget, setSnapTarget] = useState<SnapTarget>(null);
  const dragStart = useRef({ x: 0, y: 0, winX: 0, winY: 0 });
  const resizeStart = useRef({ x: 0, y: 0, width: 0, height: 0 });
  // Geometry being manipulated this gesture; applied to the DOM directly so a
  // drag/resize never re-renders the window tree, then committed on release.
  const liveGeometry = useRef({ x: win.x, y: win.y, width: win.width, height: win.height });
  const snapTargetRef = useRef<SnapTarget>(null);

  const isActive = activeWindowId === win.id;
  const AppIcon = getAppMeta(win.appId)?.icon;

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('.window-control')) return;
    focusWindow(win.id);
  }, [focusWindow, win.id]);

  const handleTitleBarMouseDown = useCallback((e: React.MouseEvent) => {
    if (win.isMaximized) return;
    if ((e.target as HTMLElement).closest('.window-control')) return;
    setIsDragging(true);
    liveGeometry.current = { x: win.x, y: win.y, width: win.width, height: win.height };
    dragStart.current = {
      x: e.clientX,
      y: e.clientY,
      winX: win.x,
      winY: win.y,
    };
  }, [win.isMaximized, win.x, win.y, win.width, win.height]);

  const handleTitleBarDoubleClick = useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('.window-control')) return;
    if (win.isMaximized) {
      restoreWindow(win.id);
    } else {
      maximizeWindow(win.id);
    }
  }, [win.id, win.isMaximized, maximizeWindow, restoreWindow]);

  const handleResizeMouseDown = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsResizing(true);
    liveGeometry.current = { x: win.x, y: win.y, width: win.width, height: win.height };
    resizeStart.current = {
      x: e.clientX,
      y: e.clientY,
      width: win.width,
      height: win.height,
    };
  }, [win.x, win.y, win.width, win.height]);

  useEffect(() => {
    if (!isDragging && !isResizing) return;

    const el = windowRef.current;

    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        const dx = e.clientX - dragStart.current.x;
        const dy = e.clientY - dragStart.current.y;
        const x = Math.min(
          Math.max(0, dragStart.current.winX + dx),
          Math.max(0, window.innerWidth - 120)
        );
        const y = Math.min(
          Math.max(0, dragStart.current.winY + dy),
          Math.max(0, window.innerHeight - DOCK_HEIGHT - 40)
        );
        liveGeometry.current.x = x;
        liveGeometry.current.y = y;
        if (el) {
          el.style.left = `${x}px`;
          el.style.top = `${y}px`;
        }

        // Edge detection drives the snap preview
        let target: SnapTarget = null;
        if (e.clientY <= SNAP_THRESHOLD) target = 'top';
        else if (e.clientX <= SNAP_THRESHOLD) target = 'left';
        else if (e.clientX >= window.innerWidth - SNAP_THRESHOLD) target = 'right';
        if (target !== snapTargetRef.current) {
          snapTargetRef.current = target;
          setSnapTarget(target);
        }
      }
      if (isResizing) {
        const dx = e.clientX - resizeStart.current.x;
        const dy = e.clientY - resizeStart.current.y;
        const width = Math.max(MIN_WIDTH, resizeStart.current.width + dx);
        const height = Math.max(MIN_HEIGHT, resizeStart.current.height + dy);
        liveGeometry.current.width = width;
        liveGeometry.current.height = height;
        if (el) {
          el.style.width = `${width}px`;
          el.style.height = `${height}px`;
        }
      }
    };

    const handleMouseUp = () => {
      const snap = snapTargetRef.current;
      if (isDragging && snap) {
        if (snap === 'top') {
          updateWindowGeometry(win.id, { isMaximized: true });
        } else {
          updateWindowGeometry(win.id, { ...getSnapGeometry(snap), isMaximized: false });
        }
      } else {
        updateWindowGeometry(win.id, { ...liveGeometry.current });
      }
      snapTargetRef.current = null;
      setSnapTarget(null);
      setIsDragging(false);
      setIsResizing(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isResizing, win.id, updateWindowGeometry]);

  // Minimized windows stay MOUNTED (hidden) so the app inside keeps its
  // state — scroll position, open tab/thread, loaded pages — and restores
  // instantly instead of remounting and refetching everything.
  const hidden = win.isMinimized;

  const style: React.CSSProperties = win.isMaximized
    ? {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: `calc(100% - ${DOCK_HEIGHT}px)`,
        zIndex: win.zIndex,
        display: hidden ? 'none' : undefined,
      }
    : {
        position: 'absolute',
        left: win.x,
        top: win.y,
        width: win.width,
        height: win.height,
        zIndex: win.zIndex,
        display: hidden ? 'none' : undefined,
      };

  const snapPreview = snapTarget ? getSnapGeometry(snapTarget) : null;

  return (
    <>
      {/* Event shield so iframes (e.g. the nsite browser) can't swallow mousemove mid-gesture */}
      {(isDragging || isResizing) && (
        <div className="fixed inset-0" style={{ zIndex: 2147483646, cursor: isDragging ? 'grabbing' : 'se-resize' }} />
      )}
      {snapPreview && (
        <div
          className="fixed rounded-xl border-2 border-primary/60 bg-primary/15 backdrop-blur-[2px] pointer-events-none transition-all duration-100"
          style={{
            left: snapPreview.x,
            top: snapPreview.y,
            width: snapPreview.width,
            height: snapPreview.height,
            zIndex: win.zIndex - 1,
          }}
        />
      )}
      <div
        ref={windowRef}
        className={cn(
          'group flex flex-col overflow-hidden bg-background/95 backdrop-blur-xl',
          'animate-in fade-in-0 zoom-in-95 duration-150',
          win.isMaximized
            ? 'border-0'
            : cn('rounded-xl border', isActive
                ? 'shadow-2xl border-white/25 ring-1 ring-white/10'
                : 'shadow-xl border-white/10'),
          isDragging && 'cursor-grabbing select-none'
        )}
        style={style}
        onMouseDown={handleMouseDown}
      >
        {/* Title Bar */}
        <div
          className={cn(
            'flex items-center justify-between pl-3 pr-2 py-2 border-b transition-colors',
            isActive ? 'bg-muted/60 border-border/60' : 'bg-muted/25 border-border/40',
            !win.isMaximized && 'cursor-grab'
          )}
          onMouseDown={handleTitleBarMouseDown}
          onDoubleClick={handleTitleBarDoubleClick}
        >
          <div className="flex items-center gap-2 min-w-0">
            {AppIcon && (
              <AppIcon className={cn('w-4 h-4 shrink-0 transition-colors', isActive ? 'text-primary' : 'text-muted-foreground')} />
            )}
            <span className={cn('text-sm font-medium truncate select-none transition-colors', !isActive && 'text-muted-foreground')}>
              {win.title}
            </span>
          </div>
          <div className="flex items-center gap-1 window-control">
            <button
              onClick={() => minimizeWindow(win.id)}
              className="p-1 rounded-md hover:bg-accent transition-colors"
              title="Minimize"
            >
              <Minus className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => win.isMaximized ? restoreWindow(win.id) : maximizeWindow(win.id)}
              className="p-1 rounded-md hover:bg-accent transition-colors"
              title={win.isMaximized ? 'Restore' : 'Maximize'}
            >
              <Square className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => closeWindow(win.id)}
              className="p-1 rounded-md hover:bg-destructive hover:text-destructive-foreground transition-colors"
              title="Close"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden min-h-0">
          {children}
        </div>

        {/* Resize Handle — generous hit area, grip brightens on hover */}
        {!win.isMaximized && (
          <div
            className="absolute bottom-0 right-0 w-5 h-5 cursor-se-resize"
            onMouseDown={handleResizeMouseDown}
          >
            <div className="absolute bottom-1 right-1 w-2.5 h-2.5 border-r-2 border-b-2 border-muted-foreground/40 rounded-br-sm transition-colors group-hover:border-muted-foreground/70" />
          </div>
        )}
      </div>
    </>
  );
}
